import { Box, Paper, styled, TextField, Typography } from "@mui/material";
import React, { useMemo } from "react";
import {
  Language,
  Visibility,
  Image,
  Theaters,
  Clear,
  Create,
  Check,
} from "@mui/icons-material";
import clsx from "clsx";
import { useViewport } from "../../../helpers";

const PREFIX = "PlaylistElement";

const classes = {
  root: `${PREFIX}-root`,
  itemTitle: `${PREFIX}-itemTitle`,
  descriptionTitle: `${PREFIX}-descriptionTitle`,
  clearButtonPosition: `${PREFIX}-clearButtonPosition`,
  visibilityButtonPosition: `${PREFIX}-visibilityButtonPosition`,
  iconSize: `${PREFIX}-iconSize`,
  editIcons: `${PREFIX}-editIcons`,
};

const StyledDiv = styled("div")(({ theme }) => ({
  [`& .${classes.root}`]: {
    position: "relative",
    width: "100%",
    minHeight: "75px",
    display: "flex",
    alignItems: "center",
    background: "rgb(66, 66, 66)",
  },

  [`& .${classes.itemTitle}`]: {
    color: "#D3AF5F",
    fontWeight: "500",
    textOverflow: "ellipsis",
    overflow: "hidden",
    whiteSpace: "nowrap",
    maxWidth: "150px",
    fontSize: "23px",
  },

  [`& .${classes.descriptionTitle}`]: {
    color: "#ffffffb3",
    textOverflow: "ellipsis",
    overflow: "hidden",
    whiteSpace: "nowrap",
    width: "105px",
    maxWidth: "100%",
    fontSize: "16px",
  },

  [`& .${classes.clearButtonPosition}`]: {
    position: "absolute",
    zIndex: "999",
    top: "5px",
    right: "8px",
    border: "none",
    cursor: "pointer",
    backgroundColor: "transparent",
    width: "32px",
    height: "30px",
  },

  [`& .${classes.visibilityButtonPosition}`]: {
    position: "absolute",
    zIndex: "999",
    bottom: "5px",
    right: "8px",
    border: "none",
    backgroundColor: "transparent",
    width: "32px",
    height: "30px",
  },

  [`& .${classes.iconSize}`]: {
    width: "24px",
    height: "24px",
    cursor: "pointer",
  },

  [`& .${classes.editIcons}`]: {
    "&:hover": {
      backgroundColor: "rgba(255, 255, 255, 0.05)",
      borderRadius: "50%",
    },
  },
}));

const iconsStyle = {
  color: "#D3AF5F",
  marginRight: "0.5rem",
};

const PlaylistElement = ({
  id,
  title,
  description,
  type,
  disableAction,
  onRemove = undefined,
  onPreview = undefined,
  handleItemModification = undefined,
  isEditing,
  hasAccess,
}) => {
  const { width } = useViewport();

  const contentTypeIcons = useMemo(
    () => ({
      video: <Theaters style={iconsStyle} />,
      image: <Image style={iconsStyle} />,
      link: <Language style={iconsStyle} />,
    }),
    []
  );

  return (
    <StyledDiv>
      <Paper
        elevation={24}
        className={classes.root}
        sx={{
          minWidth: width < 960 ? "300px" : "335px",
        }}
      >
        <Box
          sx={{
            width: width < 960 && isEditing ? "30px" : "50px",
            height: "100%",
          }}
        />
        {(onRemove || disableAction) && !isEditing && (
          <Clear
            color="primary"
            className={clsx(classes.iconSize, classes.clearButtonPosition)}
            onClick={onRemove}
          />
        )}
        {(onPreview || disableAction) && !isEditing && (
          <Visibility
            color="primary"
            className={clsx(classes.visibilityButtonPosition, classes.iconSize)}
            onClick={onPreview}
          />
        )}
        {!isEditing ? (
          <Box sx={{ display: "flex", flexDirection: "column" }}>
            <Box
              sx={{
                display: "flex",
                flexDirection: "row",
                alignItems: "center",
              }}
            >
              {contentTypeIcons[type]}
              <Typography variant="body1" className={classes.itemTitle}>
                {title}
              </Typography>
              {hasAccess && (
                <Create
                  color="primary"
                  className={clsx(classes.iconSize, classes.editIcons)}
                  onClick={() =>
                    handleItemModification(id, "editing", !isEditing)
                  }
                  sx={{ marginLeft: "0.5rem", transform: "translateY(-2px)" }}
                />
              )}
            </Box>
            <Typography variant="body1" className={classes.descriptionTitle}>
              {description}
            </Typography>
          </Box>
        ) : (
          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
            }}
          >
            <Box sx={{ display: "flex", flexDirection: "column" }}>
              <TextField
                value={title}
                placeholder="title"
                sx={{ margin: "0.5rem 0", width: "220px" }}
                onChange={(e) =>
                  handleItemModification(id, "title", e.target.value)
                }
              />
              <TextField
                value={description}
                placeholder={"description"}
                sx={{ marginBottom: "0.5rem", width: "220px" }}
                onChange={(e) =>
                  handleItemModification(id, "description", e.target.value)
                }
              />
            </Box>
            {hasAccess && (
              <Check
                color="primary"
                className={clsx(classes.iconSize, classes.editIcons)}
                onClick={() =>
                  handleItemModification(id, "editing", !isEditing, {
                    title,
                    description,
                  })
                }
                sx={{
                  marginLeft: width < 960 ? "0.25rem" : "0.5rem",
                  transform: "translateY(-2px)",
                }}
              />
            )}
          </Box>
        )}
      </Paper>
    </StyledDiv>
  );
};

export default PlaylistElement;
